'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import { formatCreditCost } from '@/features/usage/format';
import { useLocale } from '@/lib/i18n/useLocale';
import type { SkillDTO } from '../store';

interface SkillCardProps {
  skill: SkillDTO;
  selected: boolean;
  onSelect: () => void;
}

function isEnabledAnywhere(skill: SkillDTO): boolean {
  return Object.values(skill.contexts ?? {}).some((ctx) => ctx.enabled);
}

function minCostCredits(skill: SkillDTO): number {
  const costs = Object.values(skill.contexts ?? {}).map((c) => c.estimated_cost_credits);
  return costs.length ? Math.min(...costs) : 0;
}

export function SkillCard({ skill, selected, onSelect }: SkillCardProps) {
  const t = useTranslations('skills');
  const locale = useLocale();
  const enabled = isEnabledAnywhere(skill);
  const cost = minCostCredits(skill);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full text-left rounded-md border p-4 transition-colors duration-150',
        'flex flex-col gap-2',
        selected
          ? 'border-accent bg-accent/5'
          : 'border-border bg-surface hover:bg-hover hover:border-accent/40',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-medium leading-tight">{skill.name}</span>
          <span className="truncate text-xs font-mono text-muted-foreground">{skill.id}</span>
        </div>
        <span
          className={cn(
            'mt-0.5 h-2 w-2 shrink-0 rounded-full',
            enabled ? 'bg-success' : 'bg-muted-foreground/30',
          )}
          aria-label={enabled ? t('skillCard.enabled') : t('skillCard.disabled')}
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center rounded-sm bg-accent/10 px-1.5 py-0.5 text-xs font-medium text-accent capitalize">
          {skill.category}
        </span>
        {skill.source === 'user' && (
          <span className="inline-flex items-center rounded-sm bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
            {t('skillCard.custom')}
          </span>
        )}
        {cost > 0 && (
          <span className="text-xs text-muted-foreground">{t('skillCard.fromCost', { cost: formatCreditCost(cost, locale) })}</span>
        )}
      </div>
    </button>
  );
}
